import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';

interface Props { autoRun: boolean; autoNonce: number; }

export function MediaApiTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [metrics, setMetrics] = useState({
    camera: 'checking' as string,
    microphone: 'checking' as string,
    deviceOrientation: false,
    deviceMotion: false,
    geolocation: false,
    mediaDevices: false,
  });
  const autoDoneRef = useRef(false);

  function reset() {
    setRunning(false);
    autoDoneRef.current = false;
    setMetrics({ camera: 'checking', microphone: 'checking', deviceOrientation: false, deviceMotion: false, geolocation: false, mediaDevices: false });
  }

  async function start() {
    reset();
    setRunning(true);

    const hasMediaDevices = !!navigator.mediaDevices?.getUserMedia;
    const hasGeo = 'geolocation' in navigator;
    const hasOrientation = 'DeviceOrientationEvent' in window;
    const hasMotion = 'DeviceMotionEvent' in window;

    setMetrics((m) => ({
      ...m,
      mediaDevices: hasMediaDevices,
      geolocation: hasGeo,
      deviceOrientation: hasOrientation,
      deviceMotion: hasMotion,
    }));

    // Check camera/mic
    if (hasMediaDevices) {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some((d) => d.kind === 'videoinput');
        const hasMic = devices.some((d) => d.kind === 'audioinput');
        setMetrics((m) => ({
          ...m,
          camera: hasCamera ? 'available' : 'not found',
          microphone: hasMic ? 'available' : 'not found',
        }));
      } catch {
        setMetrics((m) => ({ ...m, camera: 'error', microphone: 'error' }));
      }
    } else {
      setMetrics((m) => ({ ...m, camera: 'no API', microphone: 'no API' }));
    }
  }

  function stop() {
    setRunning(false);
    let sc = 30;
    if (metrics.mediaDevices) sc += 15;
    if (metrics.camera === 'available') sc += 15;
    if (metrics.microphone === 'available') sc += 10;
    if (metrics.deviceOrientation) sc += 10;
    if (metrics.deviceMotion) sc += 10;
    if (metrics.geolocation) sc += 10;
    sc = Math.min(100, sc);

    const testId: TestId = 'mobile-media';
    reportResult(testId, {
      score: sc,
      primary: `Camera: ${metrics.camera} · Mic: ${metrics.microphone}`,
      details: metrics,
    });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  useEffect(() => {
    if (!autoRun) return;
    start();
    const timer = setTimeout(() => stop(), 3000);
    return () => clearTimeout(timer);
  }, [autoRun, autoNonce]);

  const statusIcon = (v: boolean | string) => {
    if (v === true || v === 'available') return t('supported.yes', language);
    if (v === false || v === 'not found' || v === 'no API') return t('supported.no', language);
    if (v === 'error') return 'Error';
    return v;
  };

  return (
    <TestCard title={t('mobile.media.title', language)} description={t('mobile.media.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('media.camera', language)} value={statusIcon(metrics.camera)} />
        <MetricBox label={t('media.microphone', language)} value={statusIcon(metrics.microphone)} />
        <MetricBox label={t('media.orientation', language)} value={statusIcon(metrics.deviceOrientation)} />
        <MetricBox label={t('media.motion', language)} value={statusIcon(metrics.deviceMotion)} />
      </div>
      <div className="grid grid-cols-2 gap-2.5 mt-2.5 max-sm:grid-cols-1">
        <MetricBox label={t('media.geolocation', language)} value={statusIcon(metrics.geolocation)} />
        <MetricBox label="MediaDevices API" value={statusIcon(metrics.mediaDevices)} />
      </div>
    </TestCard>
  );
}
